import loadable from "@loadable/component";
import { initializeLogger } from "@mgdx-libs/logger";
import { createContext, PropsWithChildren, useCallback, useContext, useMemo, useRef, useState } from "react";

import hasLocalStorage from "../../utils/hasLocalStorage";
import { noop } from "../../utils/noop";

const VConsoleLoader = loadable(() => import("./VConsoleLoader"), {
  resolveComponent: (components) => components.VConsoleLoader,
});

type VConsoleContextValue = {
  toggleVConsole: () => void;
  enableVConsole: boolean;
};

const VConsoleContext = createContext<VConsoleContextValue>({
  toggleVConsole: noop,
  enableVConsole: false,
});

export type VConsoleProviderProps = PropsWithChildren<EmptyObject>;

export const VConsoleProvider = ({ children }: PropsWithChildren<VConsoleProviderProps>) => {
  const [enableVConsole, setEnableVConsole] = useState<boolean>(false);

  const timeoutIdRef = useRef<number | null>(null);
  const clickedCountRef = useRef<number>(0);

  const toggleVConsole = useCallback(() => {
    const isUpdateDebugPattern =
      (process.env.NEXT_PUBLIC_ENABLE_DEBUG ?? process.env.ENABLE_DEBUG) !== "true" && hasLocalStorage();

    if (enableVConsole) {
      setEnableVConsole(false);
      if (isUpdateDebugPattern) {
        localStorage.debug = null;
      }
    } else {
      setEnableVConsole(true);
      if (isUpdateDebugPattern) {
        localStorage.debug = "app:*";
      }
    }
  }, [enableVConsole]);

  const toggleVConsoleHandler = useCallback(() => {
    if (timeoutIdRef.current) {
      window.clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }

    timeoutIdRef.current = window.setTimeout(() => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
        clickedCountRef.current = 0;
      }
    }, 150);

    clickedCountRef.current += 1;

    const buildEnv = process.env.NEXT_PUBLIC_BUILD_ENV ?? process.env.BUILD_ENV ?? "dev";
    const requiredClickCount = buildEnv === "dev" || buildEnv === "mccmstg" ? 2 : 8;
    if (clickedCountRef.current === requiredClickCount) {
      toggleVConsole();
    }
  }, [toggleVConsole]);

  const value = useMemo<VConsoleContextValue>(
    () => ({
      toggleVConsole: toggleVConsoleHandler,
      enableVConsole,
    }),
    [enableVConsole, toggleVConsoleHandler]
  );

  return (
    <VConsoleContext.Provider value={value}>
      {enableVConsole && <VConsoleLoader onReady={initializeLogger} />}
      {children}
    </VConsoleContext.Provider>
  );
};

export const useVConsole = (): VConsoleContextValue => useContext(VConsoleContext);
